import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useFrontAppIntegrationQuery } from "modules/settings/hooks/frontapp/useFrontAppIntegrationQuery"
import { useUpsertFrontAppIntegrationMutation } from "modules/settings/hooks/frontapp/useUpsertFrontAppIntegrationMutation"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertFrontAppIntegrationSchema } from "schema"
import { toast } from "sonner"
import z from "zod"

export const FrontAppIntegrationModal = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutateAsync, isPending } = useUpsertFrontAppIntegrationMutation()
  const { data, isPending: isIntegrationPending } = useFrontAppIntegrationQuery()

  const form = useForm<z.infer<typeof UpsertFrontAppIntegrationSchema>>({
    resolver: zodResolver(UpsertFrontAppIntegrationSchema),
    defaultValues: {
      token: "",
    },
    mode: "all",
  })

  function onOpenChange(isOpen: boolean = false) {
    if (isPending) return

    if (!isOpen) {
      navigate({ to: "/settings/integrations/frontapp" })
    }
  }

  async function onSubmit(data: z.infer<typeof UpsertFrontAppIntegrationSchema>) {
    await mutateAsync(data)
    toast.success(t("settings.integrations.frontapp.integrationCard.success"))
    onOpenChange(false)
  }

  if (isIntegrationPending) return null

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader className="flex flex-col gap-2 items-start">
          <DialogTitle>{t("settings.integrations.frontapp.integrationCard.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.frontapp.integrationCard.description")}{" "}
            <Popover>
              <PopoverTrigger slick>
                <span>{t("settings.integrations.frontapp.integrationCard.help.tooltip")}</span>
              </PopoverTrigger>
              <PopoverContent className="max-w-96 flex flex-col gap-2">
                <h3 className="font-medium">
                  {t("settings.integrations.frontapp.integrationCard.help.tooltip")}
                </h3>
                <ul className="list-decimal list-inside text-left text-sm space-y-1">
                  <li>{t("settings.integrations.frontapp.integrationCard.help.step1")}</li>
                  <li>{t("settings.integrations.frontapp.integrationCard.help.step2")}</li>
                  <li>{t("settings.integrations.frontapp.integrationCard.help.step3")}</li>
                </ul>
              </PopoverContent>
            </Popover>
          </DialogDescription>
        </DialogHeader>
        <div>
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t("settings.integrations.frontapp.integrationCard.form.token")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} required placeholder={data?.maskedToken} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button disabled={form.formState.isLoading || !form.formState.isValid || isPending}>
                {t("settings.integrations.frontapp.integrationCard.form.submit")}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
                {t("shared.cancel")}
              </Button>
            </DialogFooter>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
